import { BOOLEAN_SELECT_LIST_ITEMS, Checkbox, Stack, ValidationErrors, pn } from "@compass/core";
import { DatePicker, Select } from "components";
import {
  MatchReportAllPartnersFilter,
  MatchReportAllPartnersResource
} from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { MatchReportGlobalFilter } from "../MatchReportPage";

export const AllPartnersFilter = (props: {
  filter: MatchReportAllPartnersFilter;
  setFilter: (value: MatchReportAllPartnersFilter) => void;
  globalFilter: MatchReportGlobalFilter;
  setGlobalFilter: (value: MatchReportGlobalFilter) => void;
  validationErrors: ValidationErrors;
}) => {
  const [resource, setResource] = useState<MatchReportAllPartnersResource>({});
  const properties = resource.filterProperties || [];

  useEffect(() => {
    fetchResource();
  }, []);

  useEffect(() => {
    props.setFilter(
      produce(props.filter, (draft) => {
        if (props.globalFilter.dateFrom) draft.dateFrom = props.globalFilter.dateFrom;
        if (props.globalFilter.dateTo) draft.dateTo = props.globalFilter.dateTo;
        draft.isMatch = props.globalFilter.isMatch;
        draft.reviewId = props.globalFilter.reviewId;
      })
    );
  }, []);

  const fetchResource = () => {
    ApiService.matchReports.getAllPartnersResource().then((response) => {
      setResource(response);
    });
  };

  const dateFromField = () => {
    const propertyName = pn<MatchReportAllPartnersFilter>("dateFrom");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.filter.dateFrom}
        onChange={(value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateFrom = value;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.dateFrom = value;
            })
          );
        }}
      />
    );
  };

  const dateToField = () => {
    const propertyName = pn<MatchReportAllPartnersFilter>("dateTo");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.filter.dateTo}
        onChange={(value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.dateTo = value;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.dateTo = value;
            })
          );
        }}
      />
    );
  };

  const reviewField = () => {
    const propertyName = pn<MatchReportAllPartnersFilter>("reviewId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={resource.matchReviews?.find((x) => x.id === props.filter.reviewId)}
        options={resource.matchReviews}
        getOptionLabel={option => option.name}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.reviewId = value?.id;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.reviewId = value?.id;
            })
          );
        }}
      />
    );
  };

  const isMatchField = () => {
    const propertyName = pn<MatchReportAllPartnersFilter>("isMatch");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.filter.isMatch)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) => {
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.isMatch = value?.id;
            })
          );
          props.setGlobalFilter(
            produce(props.globalFilter, (draft) => {
              draft.isMatch = value?.id;
            })
          );
        }}
      />
    );
  };

  const showPartnersWithoutDataField = () => {
    const propertyName = pn<MatchReportAllPartnersFilter>("showPartnersWithoutData");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Checkbox
        label={property?.displayName}
        checked={props.filter.showPartnersWithoutData}
        onChange={(e) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.showPartnersWithoutData = e.target.checked;
            })
          )
        }
      />
    );
  };

  return (
    <Stack spacing="sm">
      {dateFromField()}
      {dateToField()}
      {reviewField()}
      {isMatchField()}
      {showPartnersWithoutDataField()}
    </Stack>
  );
};
